import {
    Box,
    Paragraph,
    SectionHeading,
    SmallSectionHeading,
    Spinner,
    Stack,
} from '@99designs/emu';
import { useIsExperimentActivated } from '@99designs/experiments';
import { FeaturesList, PackageCard } from '@99designs/marketing-ui';
import { useForm } from '../../ObservableForm';
import { useLogoPackageConfig } from './useLogoPackageConfig';
import { getContestTierColor, getContestTierName } from './utils';
import type { LogoContestTierChoiceFieldFragment } from './LogoContestTierChoiceField.generated';
import type { ContestProduct } from './types';

export function Edit({ id, label }: LogoContestTierChoiceFieldFragment) {
    const form = useForm();

    const defaultLogoTier = 'GOLD';

    if (!form.get(id)) {
        form.set(id, defaultLogoTier);
    }

    const { onChange } = form.attach(id, true);

    const { logoPackages, logoPackagesError } = useLogoPackageConfig();

    if (logoPackagesError) {
        throw logoPackagesError;
    }

    const labExperimentEnabled = useIsExperimentActivated('labs', 'enabled');

    return (
        <Stack space="1.5bu">
            <SectionHeading>{label}</SectionHeading>
            {labExperimentEnabled && (
                <>
                    <SmallSectionHeading>{__('Included in all packages')}</SmallSectionHeading>
                    <FeaturesList
                        features={[
                            __('1 finished, completely custom logo design'),
                            __('Full copyright ownership'),
                            __('Up to 3 color variations'),
                            __('Digital print and web files (RGB, CMYK, PNG, JPG, PDF)'),
                            __('The original, editable file (i.e. AI)'),
                            __('100% money back guarantee'),
                        ]}
                    />
                </>
            )}
            {logoPackages.length < 1 ? (
                <Box display={'flex'} justifyContent={'center'}>
                    <Spinner />
                </Box>
            ) : (
                sortProductsByPrice(logoPackages).map((item) => {
                    return (
                        <PackageCard
                            key={item.key}
                            id={item.id}
                            title={getContestTierName(item.tier) || item.id}
                            price={{
                                currency: item.pricing.currency,
                                amountInCents: item.pricing.amountInCents,
                            }}
                            featureColor={getContestTierColor(item.tier)}
                            isSelected={item.id === form.get(id)}
                            onSelection={(value: string) => onChange(value)}
                            tier={item.tier}
                            headingAccessibilityLevel={4}
                        >
                            <Stack space="0.5bu">
                                <Paragraph>{item.description}</Paragraph>
                                <FeaturesList features={item.details} />
                            </Stack>
                        </PackageCard>
                    );
                })
            )}
        </Stack>
    );
}

export function View({ logoContestTierChoiceValue }: LogoContestTierChoiceFieldFragment) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{logoContestTierChoiceValue}</>;
}

export function HasValue({
    logoContestTierChoiceValue,
}: LogoContestTierChoiceFieldFragment): boolean {
    return logoContestTierChoiceValue !== '';
}

export function getValue({ logoContestTierChoiceValue }: LogoContestTierChoiceFieldFragment) {
    return logoContestTierChoiceValue;
}

function sortProductsByPrice(products: ContestProduct[]) {
    return products.sort(
        (product, prevProduct) => product.pricing.amountInCents - prevProduct.pricing.amountInCents
    );
}
